import { pluralFor } from "./plural";

const p = pluralFor("en");

/**
 * The English catalogue — and, because `MessageKey` is `keyof typeof en`, **the definition of
 * what a message key is**.
 *
 * That asymmetry is the architecture: this module declares the vocabulary, `cs.ts` is typed
 * `Catalogue` and therefore cannot compile while a key is missing, and adding a string here
 * without translating it fails `pnpm typecheck` rather than shipping an English word into a
 * Czech screen. It is the same compiler-enforced completeness the audit registry and the
 * event vocabulary already rely on.
 *
 * Conventions:
 *
 * - **Values are strings, or functions returning strings.** A function is the only way a
 *   message interpolates, and its parameter list is part of the contract — `cs.ts` must take
 *   the same arguments, so a translation cannot silently drop one.
 * - **Never assemble a sentence from fragments at a call site.** Word order is not universal:
 *   `"Deactivate " + name + "?"` is untranslatable, `(name) => …` is not. Where markup has to
 *   sit inside a sentence, the sentence is split into named prefix/suffix keys whose order the
 *   component keeps — never into "words".
 * - **Counts go through `p()`**, which is `Intl.PluralRules`. English needs two forms; the
 *   Czech module needs four, which is precisely why no key may embed its own `n === 1`.
 * - **Numbers, amounts and dates are not in here.** They come from `lib/format.ts`, already
 *   formatted for the reader's locale, and arrive as arguments.
 * - **"Sales Board" is a product name and is never translated.**
 */
export const en = {
  // ─── the shell ────────────────────────────────────────────────────────────────
  "app.name": "Sales Board",
  "app.homeLink": "Sales Board — home",
  "app.logoAlt": "INFA Partner",

  "nav.main": "Main",
  "nav.pipeline": "Pipeline",
  "nav.activity": "Activity",
  "nav.clients": "Clients",
  "nav.managers": "Managers",
  "nav.signOut": "Sign out",
  "nav.language": "Language",

  "locale.cs": "Čeština",
  "locale.en": "English",

  "connection.reconnecting": "Reconnecting — changes from others may not appear, and moving cards is paused.",
  "connection.stale": "This board may be out of date. Reconnecting — moving cards is paused.",
  "session.checking": "Checking your session…",

  // ─── role, state and status vocabulary ────────────────────────────────────────
  "role.sales_manager": "Sales Manager",
  "role.head_of_sales": "Head of Sales",

  "userState.invited": "Invited",
  "userState.active": "Active",
  "userState.deactivated": "Deactivated",

  "status.open": "Open",
  "status.won": "Won",
  "status.lost": "Lost",

  // ─── words that recur everywhere ──────────────────────────────────────────────
  "common.cancel": "Cancel",
  "common.saving": "Saving…",
  "common.working": "Working…",
  "common.done": "Done",
  "common.close": "Close",
  "common.dismiss": "Dismiss",
  "common.rename": "Rename",
  "common.edit": "Edit",
  "common.saveChanges": "Save changes",
  "common.deactivate": "Deactivate",
  "common.reactivate": "Reactivate",
  "common.all": "All",
  "common.none": "None",
  "common.you": "(you)",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.showLess": "Show less",
  "common.tryAgain": "Try again",
  "common.loading": "Loading…",
  "date.choose": "Choose a date",
  "date.previousMonth": "Previous month",
  "date.nextMonth": "Next month",

  "common.paused": "Paused until the connection is back",
  "common.savingStatus": "Saving",

  /*
   * A sign-in, told relatively while that is the more useful answer. The clock survives only
   * for today and yesterday — see `formatSignIn`.
   */
  "signIn.today": (time: string) => `Today, ${time}`,
  "signIn.yesterday": (time: string) => `Yesterday, ${time}`,
  "signIn.daysAgo": (n: number) => `${n} ${p(n, { one: "day", other: "days" })} ago`,

  // ─── the four designed states ─────────────────────────────────────────────────
  "state.noResultTitle": "Nothing matches these filters",
  "state.noResultTitleScoped": (scope: string) => `Nothing matches these filters on ${scope}`,
  "state.filteringBy": (filters: string) => `Filtering by ${filters}.`,
  "state.clearFilters": "Clear filters",
  "state.errorTitle": "Something went wrong",
  "state.reference": "Reference",
  "state.referenceHint": "quote this if you contact support.",

  // ─── tables, toolbars, pagination ─────────────────────────────────────────────
  "table.search": "Search",
  "table.searchPlaceholder": "Search…",
  "table.clearSearch": "Clear search",
  "table.filter": "Filter",
  "table.filterCount": (active: number) => `Filter (${active} active)`,
  "table.clearAll": "Clear all",
  "table.rowActions": "Actions",
  "table.removeFilter": (label: string) => `Remove filter ${label}`,

  "pagination.range": (from: string, to: string, total: string) => `${from}–${to} of ${total}`,
  "pagination.rowsPerPage": "Rows per page",
  "pagination.previous": "Previous page",
  "pagination.next": "Next page",

  // ─── filter vocabulary ───────────────────────────────────────────────────────
  "filter.searchChip": (term: string) => `“${term}”`,
  "filter.valueChip": (label: string, value: string) => `${label}: ${value}`,
  "filter.owner": "Sales manager",
  "filter.client": "Client",
  "filter.stage": "Stage",
  "filter.quarter": "Quarter",
  "filter.stalledOnly": "Stalled only",
  /** The multi-select popover: what the trigger says, and its two housekeeping items. */
  "filter.selectedCount": (n: number) => `${n} selected`,
  "filter.clearSelection": "Clear selection",
  "filter.noOptions": "Nothing to choose from",
  "filter.role": "Role",
  "filter.state": "State",
  /** The activity feed's two filters: who did it, and what kind of thing it was. */
  "filter.actor": "Person",
  "filter.category": "Category",
  "filter.quarterOption": (quarter: number) => `Q${quarter}`,

  // ─── counted phrases ─────────────────────────────────────────────────────────
  "count.opportunities": (n: number) => `${n} ${p(n, { one: "opportunity", other: "opportunities" })}`,
  "count.deals": (n: number) => `${n} ${p(n, { one: "deal", other: "deals" })}`,
  "count.days": (n: number) => `${n} ${p(n, { one: "day", other: "days" })}`,
  "count.daysInStage": (n: number) => `${n} ${p(n, { one: "day", other: "days" })} in stage`,
  "count.daysShort": (n: number) => `${n}d`,
  "count.moreCards": (n: number) => `+ ${n} more`,

  // ─── the pipeline surface ────────────────────────────────────────────────────
  "pipeline.title": "Pipeline",
  "pipeline.titleYear": (year: number) => `Pipeline ${year}`,
  "pipeline.newOpportunity": "New opportunity",
  "pipeline.fullscreen": "Fullscreen",
  "pipeline.analytics": "Analytics",
  "pipeline.leavePresentation": "Leave presentation mode",
  "pipeline.leavePresentationHint": "Leave presentation mode (Esc)",
  "pipeline.searchPlaceholder": "Search by opportunity or client…",
  "pipeline.yearFailed": "The pipeline year could not be determined.",

  "view.label": "View",
  /** The one control the pipeline's view options live behind. */
  "viewOptions.label": "View options",
  "view.board": "Board",
  "view.list": "List",

  /** Cards or chips, for the same board — the condensed density the client asked for. */
  "density.label": "Card size",
  "density.normal": "Cards",
  "density.condensed": "Condensed",

  "year.label": "Pipeline year",
  "year.archive": (year: number) => `${year} (archive)`,

  // ─── the board ───────────────────────────────────────────────────────────────
  "board.region": (year: number) => `Sales board, ${year}`,
  "board.quarterHeader": (quarter: number) => `Q${quarter}`,
  "board.cell": (stage: string, quarter: number, opportunities: string, value: string) =>
    `${stage}, Q${quarter}: ${opportunities}, ${value}`,
  "board.moreCardsIn": (stage: string, quarter: number) => ` in ${stage}, Q${quarter}`,
  "board.blockedDrag": (owner: string) => `Owned by ${owner} — only they or the Head of Sales can move this.`,
  "board.oneMoveAtATime": "One move at a time — finish the one in progress first.",
  "board.moved": (name: string, target: string) => `${name} moved to ${target}`,
  "board.filteredWith": (filters: string) => `Filtered: ${filters}`,
  "board.archiveYear": "Archive year — read-only",
  "board.loadFailed": "The board could not be loaded.",
  "board.emptyTitle": (year: number) => `No opportunities on the ${year} board`,
  "board.emptyDescription":
    "An opportunity takes four values: client, name, expected revenue and expected close date. Its stage and quarter follow from there.",
  "board.noResultScope": (year: number) => `the ${year} board`,

  // ─── the card ────────────────────────────────────────────────────────────────
  "card.stalled": "Stalled",
  "card.stalledFor": (days: number) => `Stalled for ${days} ${p(days, { one: "day", other: "days" })}`,
  "card.stalledPrefix": "Stalled, ",
  "card.stalledTooltip": (days: string) => `Stalled — ${days} in stage`,
  "card.stalledAria": (days: string) => `stalled, ${days} in stage`,
  "card.closedWon": "closed won",
  "card.closedLost": "closed lost",
  "card.ownedBy": (owner: string) => `owned by ${owner}`,
  "card.notMovable": "you cannot move this",

  // ─── the opportunity list ────────────────────────────────────────────────────
  "opportunities.title": "Opportunities",
  "opportunities.caption": "Opportunities",
  "opportunities.loading": "Loading opportunities…",
  "opportunities.loadFailed": "The pipeline could not be loaded.",
  "opportunities.emptyTitle": "No opportunities yet",
  "opportunities.emptyTitleYear": (year: number) => `No opportunities on the ${year} pipeline`,
  "opportunities.emptyDescription":
    "An opportunity takes four values: client, name, expected revenue and expected close date. Everything else follows.",
  "opportunities.noResultScope": (year: number) => `the ${year} pipeline`,

  // ─── column headers, shared across screens ───────────────────────────────────
  "column.client": "Client",
  "column.clientName": "Client name",
  "column.opportunity": "Opportunity",
  "column.stage": "Stage",
  "column.expectedRevenue": "Expected revenue",
  "column.expectedClose": "Expected close",
  "column.owner": "Owner",
  "column.name": "Name",
  "column.email": "Email",
  "column.role": "Role",
  "column.colour": "Colour",
  "column.state": "State",
  "column.lastSignIn": "Last sign-in",
  "column.openOpportunities": "Open opportunities",
  "column.openPipeline": "Open pipeline",
  "column.order": "Order",
  "column.type": "Type",

  // ─── creating an opportunity ─────────────────────────────────────────────────
  "opportunityForm.title": "New opportunity",
  "opportunityForm.submit": "Create opportunity",
  "opportunityForm.client": "Client",
  "opportunityForm.chooseClient": "Choose a client…",
  "opportunityForm.name": "Opportunity",
  "opportunityForm.revenue": "Expected revenue (CZK)",
  "opportunityForm.date": "Expected close date",
  "opportunityForm.owner": "Owner",
  "opportunityForm.ownerSelf": "You",

  // ─── the opportunity detail ──────────────────────────────────────────────────
  "opportunity.loading": "Loading opportunity…",
  "opportunity.notFound": "Opportunity not found",
  "opportunity.loadFailed": "That opportunity could not be loaded.",
  "opportunity.backToBoard": "Back to the board",
  "opportunity.reopen": "Reopen opportunity",
  "opportunity.delete": "Delete opportunity",
  "opportunity.name": "Opportunity name",
  "opportunity.stage": "Stage",
  "opportunity.expectedClose": "Expected close",
  "opportunity.expectedCloseDate": "Expected close date",
  "opportunity.expectedRevenue": "Expected revenue",
  "opportunity.salesManager": "Sales manager",
  "opportunity.actualClose": "Actual close",
  "opportunity.lastModified": "Last modified",
  "opportunity.notes": "Notes",
  "opportunity.noNote": "No note.",
  "opportunity.notePlaceholder": "—",
  "opportunity.lostReason": "Why it was lost",
  "opportunity.lostReasonPlaceholder": "Say why this was lost…",
  "opportunity.lostReasonTooShort": (min: number) => `Say why in at least ${min} characters`,
  "opportunity.lostReasonRequired": "This opportunity is now lost. Say why before continuing.",
  "opportunity.lostReasonFor": (name: string) => `Why was ${name} lost?`,
  "opportunity.lostReasonRemaining": (n: number) => `${n} more to explain`,
  "opportunity.addNote": "+ Add note",
  "opportunity.showMore": "Show more info",
  "opportunity.history": "History",
  "opportunity.historyLoading": "Loading history…",
  "opportunity.historyFailed": "The history could not be loaded.",
  "opportunity.openProfile": (name: string) => `Open ${name}'s profile`,
  "opportunity.openManager": "Open manager",
  "opportunity.reassignTitle": (name: string) => `Reassign to ${name}?`,
  "opportunity.reassignConsequence": (name: string) =>
    `${name} becomes responsible for this opportunity, and it moves to their colour on the board and into their ` +
    `filter. The stage age does not reset — a deal that has sat for fifty days has still sat for fifty days after ` +
    `it changes hands.`,
  "opportunity.reassignConfirm": "Reassign opportunity",
  "opportunity.deleteTitle": (name: string) => `Delete ${name}?`,
  "opportunity.deleteConsequence":
    "It leaves the pipeline and every total, but its history is kept. Only the Head of Sales can restore it.",

  // ─── the audit timeline ──────────────────────────────────────────────────────
  "audit.noHistory": "No history yet.",
  "audit.showAll": (total: number) => `Show all history (${total})`,
  "audit.repeated": (times: number) => `×${times}`,
  /**
   * What sits between the actor's name and what they did.
   *
   * A space in English, because the label is a verb phrase reading straight on from the name
   * ("Hana changed the name"). Czech nominalises instead — its verbs would have to agree with
   * the actor's gender, which no audit row records — so it needs a dash.
   */
  "audit.actorSeparator": " ",

  "audit.created": "created this opportunity",
  "audit.client_changed": "changed the client",
  "audit.name_changed": "changed the name",
  "audit.nip_changed": "changed expected revenue",
  "audit.expected_close_date_changed": "changed the expected close date",
  "audit.stage_changed": "moved the stage",
  "audit.closed_won": "closed this as won",
  "audit.closed_lost": "closed this as lost",
  "audit.reopened": "reopened this",
  "audit.owner_reassigned": "reassigned this to",
  "audit.notes_changed": "changed the note",
  "audit.lost_reason_changed": "recorded why it was lost",
  "audit.notes_added": "added a note",
  "audit.notes_removed": "removed the note",
  "audit.deleted": "deleted this",
  "audit.restored": "restored this",
  "audit.user_invited": "invited this user",
  "audit.invitation_resent": "re-sent the invitation",
  "audit.invitation_revoked": "revoked the invitation",
  "audit.credential_created": "set their password",
  "audit.password_reset": "reset their password",
  "audit.role_changed": "changed the role",
  "audit.user_deactivated": "deactivated this user",
  "audit.user_reactivated": "reactivated this user",
  "audit.color_changed": "changed their board colour",
  "audit.stage_created": "added this stage",
  "audit.stage_renamed": "renamed this stage",
  "audit.stage_type_changed": "changed what this stage means",
  "audit.stage_reordered": "moved this stage",
  "audit.stage_color_changed": "changed this stage's colour",
  "audit.stage_deactivated": "deactivated this stage",
  "audit.stage_reactivated": "reactivated this stage",
  "audit.setting_changed": "changed this setting",

  /*
   * The **feed** phrasing of each event: the same events as `audit.*` above, worded so the
   * record follows the verb — "created opportunity «Data warehouse assessment»" rather than
   * "created this opportunity".
   *
   * Two sets, deliberately. Inside one record's own history the record is a given, and
   * naming it in every row would be a stutter; a company-wide feed has the opposite problem —
   * every row is about something else, and "deleted this" names nothing at all.
   */
  "feed.created": "created opportunity",
  "feed.client_changed": "changed the client of",
  "feed.name_changed": "renamed",
  "feed.nip_changed": "changed the expected revenue of",
  "feed.expected_close_date_changed": "changed the expected close date of",
  "feed.stage_changed": "moved",
  "feed.closed_won": "closed as won",
  "feed.closed_lost": "closed as lost",
  "feed.reopened": "reopened",
  "feed.owner_reassigned": "reassigned",
  "feed.notes_changed": "changed the note on",
  "feed.lost_reason_changed": "recorded why this was lost:",
  "feed.notes_added": "added a note to",
  "feed.notes_removed": "removed the note from",
  "feed.deleted": "deleted opportunity",
  "feed.restored": "restored opportunity",
  "feed.user_invited": "invited",
  "feed.invitation_resent": "re-sent the invitation to",
  "feed.invitation_revoked": "revoked the invitation for",
  "feed.credential_created": "set the password for",
  "feed.password_reset": "reset the password for",
  "feed.role_changed": "changed the role of",
  "feed.user_deactivated": "deactivated",
  "feed.user_reactivated": "reactivated",
  "feed.color_changed": "changed the board colour of",
  "feed.stage_created": "added stage",
  "feed.stage_renamed": "renamed stage",
  "feed.stage_type_changed": "changed the meaning of stage",
  "feed.stage_reordered": "moved stage",
  "feed.stage_color_changed": "changed the colour of stage",
  "feed.stage_deactivated": "deactivated stage",
  "feed.stage_reactivated": "reactivated stage",
  "feed.setting_changed": "changed setting",

  // ─── the company-wide activity feed ──────────────────────────────────────────
  "activity.title": "Activity",
  "activity.searchPlaceholder": "Search activity",
  "activity.description": "Everything that has happened, newest first.",
  "activity.loading": "Loading activity…",
  "activity.loadFailed": "The activity feed could not be loaded.",
  "activity.emptyTitle": "Nothing has happened yet",
  "activity.emptyDescription":
    "This is the record of every change made in the application. As soon as anyone raises, moves or edits something, it appears here.",
  "activity.categoryOpportunities": "Opportunities",
  "activity.categoryAdministration": "Administration",
  "activity.categoryConfiguration": "Configuration",
  /** A deleted record's events stay in the feed; the row says so in words, not by colour. */
  "activity.deletedRecord": "(deleted)",

  // ─── the lifecycle dialogs ───────────────────────────────────────────────────
  "move.quarterHint": "The quarter follows from this date — the server derives it.",
  "move.derivedPrefix": "This date is",
  "move.derivedSuffix": ".",
  "move.contradictsColumn": (dropped: number, derived: number) =>
    `You dropped the card in Q${dropped}, but this date places it in Q${derived}. The date wins — the card will appear there.`,
  "move.crossesYear": (to: number, from: number) =>
    `This moves the card to the ${to} board, so it will leave the ${from} board entirely.`,

  "close.titleWon": (name: string) => `Close ${name} as won?`,
  "close.titleLost": (name: string) => `Close ${name} as lost?`,
  "close.submitWon": "Close as won",
  "close.submitLost": "Close as lost",
  "close.actualCloseDate": "Actual close date",

  "reopen.title": (name: string) => `Reopen ${name}?`,
  "reopen.description": (stage: string, from: number, to: number) => `Into ${stage}, and from Q${from} to Q${to}.`,
  "reopen.consequence": (stage: string) =>
    `It returns to ${stage} and to operational pipeline totals, its actual close date is cleared, and its stage timer restarts now.`,
  "reopen.submit": "Reopen opportunity",
  "reopen.newDate": "New expected close date",

  // ─── the inline editing kit ──────────────────────────────────────────────────
  "inline.clickToEdit": (field: string) => `Click to edit ${field.toLowerCase()}`,
  "inline.textareaHint": "Saved when you click away. Escape discards it.",

  // ─── clients ─────────────────────────────────────────────────────────────────
  "clients.title": "Clients",
  "clients.new": "New client",
  "clients.searchPlaceholder": "Search clients…",
  "clients.loading": "Loading clients…",
  "clients.loadFailed": "The client list could not be loaded.",
  "clients.emptyTitle": "No clients yet",
  "clients.emptyDescription":
    "Clients are the registry every opportunity references. Add the first one to start recording deals.",
  "clients.caption": "Clients",
  "clients.active": "Active",
  "clients.inactive": "Inactive",

  "client.label": "Client",
  "client.loading": "Loading client…",
  "client.notFound": "Client not found",
  "client.loadFailed": "That client could not be loaded.",
  "client.backToClients": "Back to clients",
  "client.opportunities": "Opportunities",
  "client.opportunitiesCaption": (name: string) => `Opportunities for ${name}`,
  "client.noOpportunities": "No opportunities yet",
  "client.noOpportunitiesFor": (name: string) => `Nothing has been raised against ${name}.`,
  "client.deactivateTitle": (name: string) => `Deactivate ${name}?`,
  "client.reactivateTitle": (name: string) => `Reactivate ${name}?`,
  "client.deactivateConsequence": (open: string) =>
    `It stops appearing when raising new work. Its ${open} are unaffected.`,
  "client.reactivateConsequence": "It becomes available again when raising new work.",
  "client.deactivateConfirm": "Deactivate client",
  "client.reactivateConfirm": "Reactivate client",

  "clientForm.createTitle": "New client",
  "clientForm.renameTitle": "Rename client",
  "clientForm.createDescription": "Clients are shared — everyone selects from the same registry.",
  "clientForm.createSubmit": "Create client",
  "clientForm.renameSubmit": "Save name",
  "clientForm.name": "Client name",
  "clientForm.nameRequired": "Enter a client name",
  "clientForm.duplicateTitle": "This looks like an existing client",
  "clientForm.inactiveTag": "(inactive)",
  "clientForm.createAnyway": "Create anyway",

  // ─── the team-member profile ─────────────────────────────────────────────────
  "team.loading": "Loading profile…",
  "team.notFound": "Person not found",
  "team.loadFailed": "That profile could not be loaded.",
  "team.state": "State",
  "team.openOpportunities": "Open opportunities",
  "team.openPipeline": "Open pipeline",
  "team.pipelineLoading": "Loading their pipeline…",
  "team.nothingOpen": "Nothing open",
  "team.nothingOpenFor": (name: string) => `${name} owns no open opportunities on the pipeline right now.`,
  "team.ownedCaption": (name: string) => `Open opportunities owned by ${name}`,
  "team.showingOf": (shown: number, total: number) =>
    `Showing ${shown} of ${total}. Filter the pipeline by this manager to see them all.`,
  "team.accountHistory": "Account history",
  "team.sections": "Profile sections",
  "team.accountHistoryNote": "Invitations, role and state changes. Visible to the Head of Sales only.",
  "team.accountHistoryLoading": "Loading account history…",
  "team.accountHistoryFailed": "The account history could not be loaded.",

  // ─── user administration ─────────────────────────────────────────────────────
  "users.title": "Users",
  "users.new": "New user",
  "users.settingsLink": "Settings",
  "users.searchPlaceholder": "Search by name or email…",
  "users.loading": "Loading users…",
  "users.loadFailed": "The user list could not be loaded.",
  "users.emptyTitle": "No users yet",
  "users.emptyDescription": "Users are created by invitation. Add the first one to give someone access.",
  "users.caption": "Users",
  "users.editTitle": (name: string) => `Edit ${name}`,
  "users.ownRoleLocked": "You cannot change your own role.",
  "users.ownStateLocked": "You cannot deactivate your own account.",
  "users.rowActions": (name: string) => `Actions for ${name}`,
  /** The linked count: a screen reader hears the destination, not a bare number. */
  "users.openOpportunitiesFor": (name: string, n: number) =>
    `${n} open ${p(n, { one: "opportunity", other: "opportunities" })} owned by ${name} — open the board`,
  "users.invite": "Invite",
  "users.reinvite": "Re-invite",
  "users.revokeInvitation": (name: string) => `Revoke invitation for ${name}`,
  "users.invitationTitle": (name: string) => `Invitation for ${name}`,
  "users.invitationConsequence": "Any previous link for this user has been cancelled.",
  "users.invitationLinkFor": (name: string) => `Invitation link for ${name}`,
  "users.deactivateTitle": (name: string) => `Deactivate ${name}?`,
  "users.deactivateConsequence": (owned: string) =>
    `They lose access immediately — every session and any outstanding invitation is revoked. They keep ownership of ` +
    `${owned} and their history is unaffected.`,
  "users.deactivateConfirm": "Deactivate user",
  "users.invitedAt": (when: string) => `invited ${when}`,
  "users.entraOnly": "Entra ID only",

  "userForm.title": "New user",
  "userForm.description": "Users are created by invitation — there is no sign-up.",
  "userForm.submit": "Create user",
  "userForm.name": "Name",
  "userForm.email": "Email",
  "userForm.role": "Role",
  "userForm.sendInvitation": "Send an invitation email now",
  "userForm.sendInvitationHint": "You will get the link either way, so you can pass it on yourself.",
  "userForm.createdTitle": (name: string) => `${name} created`,
  "userForm.emailSent": "An invitation email is on its way.",
  "userForm.emailNotSent": "No email was sent — hand this link over yourself.",
  "invitation.validUntil": (when: string) =>
    `Valid until ${when}. Single use — issuing a new link cancels this one.`,

  // ─── the manager palette ─────────────────────────────────────────────────────
  "colour.rose": "Rose",
  "colour.amber": "Amber",
  "colour.lime": "Lime",
  "colour.emerald": "Emerald",
  "colour.cyan": "Cyan",
  "colour.blue": "Blue",
  "colour.violet": "Violet",
  "colour.fuchsia": "Fuchsia",
  "colour.stone": "Stone",
  "colour.none": "None",
  "colour.forUser": (name: string) => `Colour for ${name}: `,
  "colour.forNewUser": "Colour: ",
  "colour.forStage": (name: string) => `Colour for stage ${name}: `,
  "colour.sharedWarning": "Shares this colour with another manager",
  "colour.neutralNote": "Cards stay neutral until a manager has a colour.",
  "settings.title": "Settings",
  "settings.tabsLabel": "Settings sections",
  "settings.stagesTitle": "Sales stages",
  "settings.stagesDescription": "The rows of the board, in order. Closed stages always follow the open ones.",
  "settings.newStage": "New stage",
  "settings.stagesLoading": "Loading stages…",
  "settings.stagesFailed": "The stages could not be loaded.",
  "settings.stagesCaption": "Sales stages",
  "settings.stageBlockedHolds": (held: string) => `Holds ${held} — move them out to change or retire it.`,
  "settings.stageBlockedOnlyOpen": "The only open stage — the board needs one of each.",
  "settings.stageBlockedOnlyWon": "The only won stage — the board needs one of each.",
  "settings.stageBlockedOnlyLost": "The only lost stage — the board needs one of each.",
  "settings.stageActive": "Active",
  "settings.stageInactive": "Inactive",
  "settings.moveUp": (name: string) => `Move ${name} up`,
  "settings.alreadyFirst": "Already the first stage.",
  "settings.alreadyLast": "Already the last stage.",
  "settings.stageRowActions": (name: string) => `Actions for ${name}`,
  "settings.editStageTitle": (name: string) => `Edit ${name}`,
  "settings.moveDown": (name: string) => `Move ${name} down`,
  "settings.addStage": "Add stage",
  "settings.stageName": "Name",
  "settings.stageType": "Type",
  "settings.stageTypeOpen": "Open — work in progress",
  "settings.stageTypeWon": "Won — a closed outcome",
  "settings.stageTypeLost": "Lost — a closed outcome",
  "settings.stageTypeHint": "A closed stage records an outcome and always sits after the open ones.",
  "settings.stageTypeDialogDescription":
    "An opportunity's status follows from the stage it is in, so this changes what the board reports.",
  "settings.stageMovesToOpen": "It moves to the end of the open stages.",
  "settings.stageMovesToClosed": "It moves to the closed stages, after every open one.",
  "settings.deactivateStageTitle": (name: string) => `Deactivate ${name}?`,
  "settings.deactivateStageConsequence":
    "The row leaves the board for everyone. Its history is kept, and it can be reactivated.",
  "settings.deactivateStageConfirm": "Deactivate stage",
  "settings.reactivateStageTitle": (name: string) => `Reactivate ${name}?`,
  "settings.reactivateStageConsequence":
    "The row returns to the board for everyone, after the other stages of its kind.",
  "settings.reactivateStageConfirm": "Reactivate stage",
  "settings.reorderTitle": (name: string) => `Move ${name}?`,
  "settings.reorderConsequence": "This changes the board's row order for everyone.",
  "settings.reorderConfirm": "Change the order",

  "settings.thresholdTitle": "Stalled threshold",
  "settings.thresholdDescription":
    "How long an opportunity may sit in one stage before the board marks it stalled.",
  "settings.thresholdLoading": "Loading settings…",
  "settings.thresholdFailed": "The settings could not be loaded.",
  "settings.thresholdDays": "Days in a stage",
  "settings.thresholdRange": (min: number, max: number) => `Give a whole number between ${min} and ${max}.`,
  "settings.thresholdWarn": (days: number) =>
    `Below ${days} days, most active opportunities will read as stalled.`,
  /** Split so the emphasised word keeps its markup without the sentence being assembled. */
  "settings.thresholdRetroPrefix": "This applies",
  "settings.thresholdRetroEmphasis": "retrospectively",
  "settings.thresholdRetroSuffix":
    ": every open opportunity is reclassified immediately, on every board, not only the ones that stall from now on.",
  "settings.thresholdSave": "Save threshold",
  "settings.thresholdLastChanged": (when: string) => `Last changed ${when}`,
  "settings.thresholdLastChangedBy": (when: string, who: string) => `Last changed ${when} by ${who}`,

  // ─── analytics ───────────────────────────────────────────────────────────────
  "analytics.detailedView": "Aggregates",
  "analytics.tabPhase": "By phase",
  "analytics.tabManager": "By manager",
  /** Beside a by-manager bar, naming the part of that manager's money that is stuck. */
  "analytics.stalledAmount": (amount: string) => `${amount} stalled`,
  "analytics.noManagers": "No open pipeline to break down in this view.",

  "strip.openCount": "Open opportunities",
  "strip.pipelineValue": "Pipeline value",
  "strip.closing": (quarter: string) => `Closing ${quarter}`,
  "strip.closingThisQuarter": "Closing this quarter",
  "strip.stalled": "Stalled",
  "strip.largest": "Largest opportunity",
  "strip.loadFailed": "The summary could not be loaded.",
  "strip.loading": "Loading summary…",

  // ─── feedback ────────────────────────────────────────────────────────────────
  "toast.genericFailure": "That did not work.",
  "toast.staleTitle": "Someone else changed this first.",
  "toast.staleDescription": "Their change is now on screen — check it, then make yours again.",
  "toast.reference": (requestId: string) => `Reference ${requestId}`,
  "toast.created": (name: string) => `${name} created`,
  "toast.opportunityDeleted": "Opportunity deleted",
  "toast.savedName": "Name saved",
  "toast.savedRevenue": "Expected revenue saved",
  "toast.savedCloseDate": "Expected close date saved",
  "toast.savedClient": "Client saved",
  "toast.savedNote": "Note saved",
  "toast.removedNote": "Note removed",
  "toast.savedOpportunity": "Opportunity saved",
  "toast.reassignedTo": (name: string) => `Reassigned to ${name}`,
  "toast.closedWon": "Closed as won",
  "toast.closedLost": "Closed as lost",
  "toast.reopened": "Reopened",
  "toast.clientRenamed": "Client renamed",
  "toast.clientReactivated": "Client reactivated",
  "toast.clientDeactivated": "Client deactivated",
  "toast.invitationSentTo": (email: string) => `Invitation sent to ${email}`,
  "toast.invitationEmailSent": "Invitation email sent",
  "toast.invitationLinkReady": "Invitation link ready to copy",
  "toast.invitationRevoked": "Invitation revoked",
  "toast.roleChanged": "Role changed",
  "toast.userReactivated": "User reactivated",
  "toast.userDeactivated": "User deactivated",
  "toast.colourSaved": "Colour saved",
  "toast.stageAdded": "Stage added",
  "toast.stageRenamed": "Stage renamed",
  "toast.stageTypeChanged": "Stage type changed",
  "toast.stageUpdated": "Stage updated",
  "toast.orderChanged": "Order changed",
  "toast.settingSaved": "Setting saved",

  // ─── API failures, by code ───────────────────────────────────────────────────
  //
  // A *partial* map, deliberately: a code whose server message carries the specifics
  // ("That client does not exist") is better read from the server than replaced by a
  // generic line here. See `errorMessage.ts`.
  "error.UNAUTHENTICATED": "Your session has expired. Sign in again.",
  "error.FORBIDDEN": "You are not allowed to do that.",
  "error.CONFLICT": "That conflicts with something already recorded.",
  "error.RATE_LIMITED": "Too many attempts. Wait a moment, then try again.",
  "error.UNAVAILABLE": "The server could not be reached. Check your connection, then try again.",
  "error.INTERNAL": "Something went wrong on the server. Try again.",

  // ─── signing in ──────────────────────────────────────────────────────────────
  "auth.signInLead": "Sign in to continue.",
  "auth.continueWithMicrosoft": "Continue with Microsoft",
  "auth.or": "or",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.signIn": "Sign in",
  "auth.signingIn": "Signing in…",
  "auth.forgot": "Forgot your password?",
  "auth.signInFailed": "Could not sign in. Try again.",
  "auth.devTitle": "Development sign-in",
  "auth.devDescription": "Enter as a seeded user without a password. Enabled by AUTH_MODE=dev.",
  "auth.unprotectedTitle": "Unprotected access is enabled",
  "auth.unprotectedDescription":
    "This deployment lets anyone sign in without credentials. Turn it off before it holds real data.",
  "auth.noSeededUsersPrefix": "No seeded users — run",
  "auth.noSeededUsersSuffix": ".",

  "setPassword.lead": "Choose a password for your account.",
  "setPassword.deadLead": "This link cannot be used.",
  "setPassword.settingFor": "Setting a password for",
  "setPassword.newPassword": "New password",
  "setPassword.confirm": "Confirm password",
  "setPassword.hint": (min: number) => `At least ${min} characters. Length beats complexity.`,
  "setPassword.submit": "Set password",
  "setPassword.tooShort": (min: number) => `Use at least ${min} characters`,
  "setPassword.mismatch": "The two passwords do not match",
  "setPassword.missingToken": "This link is missing its token. Ask for a new one.",
  "setPassword.failed": "That did not work. Try again.",
  "setPassword.requestNew": "Request a new link",
  "setPassword.backToSignIn": "Back to sign in",
  "setPassword.expired": "This link has expired.",
  "setPassword.revoked": "This link was cancelled by an administrator.",
  "setPassword.redeemed": "This link has already been used. Sign in with your password, or request a reset.",
  "setPassword.deactivated": "This account has been deactivated.",
  "setPassword.invalid": "This link is not valid.",

  "forgot.lead": "Enter your email and we will send a reset link.",
  "forgot.sentLead": "Check your inbox.",
  /** Split around the emphasised address; the component keeps the order. */
  "forgot.sentPrefix": "If",
  "forgot.sentSuffix": "has an account with a password, a reset link is on its way. It is valid for two hours.",
  "forgot.submit": "Send reset link",
  "forgot.sending": "Sending…",

  // ─── document titles ─────────────────────────────────────────────────────────
  //
  // "<View> · Sales Board", and "<Record name> · Sales Board" while a detail is open. The
  // product name is not translated, so only the leading half comes from the catalogue.
  "title.document": (subject: string) => `${subject} · Sales Board`,
} satisfies Record<string, string | ((...args: never[]) => string)>;
